import { LOGO_URL } from "@/constants/roles";
import { resolveImg } from "@/lib/imageUtils";

// SIHMT logo — served locally with a real transparent PNG background so it
// works cleanly on light AND dark surfaces (no CSS blend-mode hacks needed).
// Sidecar text renders the product wordmark to the right, sized to match the
// logo height:
//   • Line 1  – "Steward Learning Cloud"                (navy #101876)
//   • Line 2  – "Healthcare LMS & Certification Platform" (green #329112)
//
// When `orgLogo` is provided (a URL or a storage path), we render the
// institute logo to the right of the SIHMT mark with a subtle vertical
// divider — this is what learners of a white-labelled institute see in
// their sidebar.
export function Logo({ variant = "full", className = "", inverted = false, showText = false, orgLogo = "" }) {
  const hasOrg = !!orgLogo;
  // When both logos need to fit in a narrow sidebar (compact variant),
  // shrink the SIHMT mark a notch, hide the wordmark text, and stack the
  // two logos side-by-side. Full variant keeps the original hero size and
  // renders the institute logo to the right of the wordmark.
  const height = variant === "compact"
    ? (hasOrg ? "h-8" : "h-10 sm:h-11")
    : "h-12 sm:h-14 md:h-16";
  const orgHeight = variant === "compact" ? "h-8" : "h-10 sm:h-12";
  const wrap = inverted
    ? "inline-flex items-center gap-3 rounded-2xl bg-white px-4 py-2 shadow-lg shadow-black/10"
    : "inline-flex items-center gap-2";
  const titleSize = variant === "compact"
    ? "text-sm sm:text-base"
    : "text-base sm:text-lg md:text-xl";
  const subSize = variant === "compact"
    ? "text-[10px] sm:text-[11px]"
    : "text-[11px] sm:text-xs md:text-[13px]";
  const resolvedOrgLogo = orgLogo ? resolveImg(orgLogo) : "";
  // In compact + hasOrg mode we hide the wordmark to give the org logo room.
  const shouldShowText = showText && !(variant === "compact" && hasOrg);
  return (
    <div className={`${wrap} ${className}`}>
      <img
        src={LOGO_URL}
        alt="Steward Learning Cloud · Healthcare LMS & Certification Platform"
        className={`${height} w-auto object-contain shrink-0`}
        data-testid="brand-logo"
        draggable={false}
      />
      {shouldShowText && (
        <div className="hidden sm:flex flex-col justify-center leading-[1.15]" data-testid="brand-name">
          <span className={`font-black tracking-tight text-[#101876] ${titleSize}`}>
            Steward Learning Cloud
          </span>
          <span className={`font-semibold tracking-tight text-[#329112] ${subSize}`}>
            Healthcare LMS &amp; Certification Platform
          </span>
        </div>
      )}
      {resolvedOrgLogo && (
        <>
          <span className="block h-8 w-px bg-slate-200" aria-hidden="true" />
          <img
            src={resolvedOrgLogo}
            alt="Institute logo"
            className={`${orgHeight} w-auto max-w-[6rem] object-contain shrink-0`}
            data-testid="brand-org-logo"
            draggable={false}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        </>
      )}
    </div>
  );
}
